// CeramicCoatingPage.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Sparkles, Clock, Star, ChevronRight, Check } from 'lucide-react';
import ceramic from './images/ceramicCoating.jpg'
import polish from './images/polish.png'

const CeramicCoatingPage = () => {
  const navigate = useNavigate();

  // Function to navigate to booking page with specific ceramic coating package selected
  const navigateToBookingWithPackage = (packageId) => {
    navigate('/book', { 
      state: { 
        preSelectedService: 'ceramic',
        serviceType: packageId,
        serviceCategory: 'ceramic'
      } 
    });
  };

  // Function to navigate to estimate page with ceramic coating selected
  const navigateToEstimate = () => {
    navigate('/estimate', { 
      state: { 
        preSelectedService: 'ceramic',
        serviceCategory: 'ceramic'
      } 
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-64 md:h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ceramic}
            alt="Ceramic Coating Protection"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
        </div>
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Professional Ceramic Coating
          </h1>
          <p className="text-white/90 max-w-xl">
            Long-lasting protection with incredible shine for your vehicle
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                Superior Protection For Your Vehicle
              </h2>
              <p className="text-gray-700 mb-6">
                Our ceramic coating creates a permanent bond with your vehicle's paint, 
                providing superior protection against environmental hazards and making 
                maintenance easier than ever.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-semibold">Long-Lasting Protection</h3>
                    <p className="text-gray-600 text-sm">
                      Protection that lasts years, not months like traditional waxes
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles className="h-6 w-6 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-semibold">Enhanced Gloss & Depth</h3>
                    <p className="text-gray-600 text-sm">
                      A mirror-like finish that enhances your vehicle's appearance
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-6 w-6 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-semibold">Easy Maintenance</h3>
                    <p className="text-gray-600 text-sm">
                      Hydrophobic properties mean your car stays cleaner longer
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => navigate('/book', { 
                    state: { 
                      preSelectedService: 'ceramic'
                    } 
                  })}
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:shadow-lg flex items-center"
                >
                  Schedule Coating Service
                  <ChevronRight className="h-5 w-5 ml-2" />
                </button>
                <button 
                  onClick={navigateToEstimate}
                  className="border border-blue-500 text-blue-500 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:bg-blue-50"
                >
                  Get Free Estimate
                </button>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img
                src={polish}
                alt="Ceramic coating application"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 bg-gray-50 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">The Ceramic Coating Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our professional-grade ceramic coating application follows a detailed process to ensure maximum protection and durability
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 font-semibold text-lg">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Preparation</h3>
              <p className="text-gray-600">
                Thorough wash and clay bar treatment to remove contaminants. Paint correction to eliminate swirls and imperfections.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 font-semibold text-lg">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Application</h3>
              <p className="text-gray-600">
                Professional application of ceramic coating with precise techniques to ensure even coverage and optimal bonding.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 font-semibold text-lg">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Curing & Inspection</h3>
              <p className="text-gray-600">
                Proper curing time followed by meticulous inspection to ensure complete coverage and perfect finish.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Package Comparison */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ceramic Coating Packages</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose the right level of protection for your vehicle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Standard Package */}
            <div className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="bg-gray-50 p-4 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800">Standard Protection</h3>
                <p className="text-gray-600 text-sm">1-year durability</p>
              </div>
              <div className="p-6">
                <div className="text-2xl font-bold text-gray-900 mb-4">$599</div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Single layer of ceramic coating</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Basic paint preparation</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Exterior surfaces only</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">6-month inspection</span>
                  </li>
                </ul>
                <button 
                  onClick={() => navigateToBookingWithPackage('ceramic_standard')}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg font-medium hover:bg-blue-600 transition-all"
                >
                  Select Package
                </button>
              </div>
            </div>

            {/* Premium Package */}
            <div className="border-2 border-blue-500 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 transform scale-105 relative">
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                MOST POPULAR
              </div>
              <div className="bg-gradient-to-r from-blue-500 to-cyan-400 p-4">
                <h3 className="text-xl font-semibold text-white">Premium Protection</h3>
                <p className="text-white/80 text-sm">3-year durability</p>
              </div>
              <div className="p-6">
                <div className="text-2xl font-bold text-gray-900 mb-4">$899</div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Two layers of ceramic coating</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">One-step paint correction</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Wheels and trim protection</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Annual inspections</span>
                  </li>
                </ul>
                <button 
                  onClick={() => navigateToBookingWithPackage('ceramic_premium')}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-2 rounded-lg font-medium hover:shadow-md transition-all"
                >
                  Select Package
                </button>
              </div>
            </div>

            {/* Elite Package */}
            <div className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="bg-gray-800 p-4">
                <h3 className="text-xl font-semibold text-white">Elite Protection</h3>
                <p className="text-white/80 text-sm">5-year durability</p>
              </div>
              <div className="p-6">
                <div className="text-2xl font-bold text-gray-900 mb-4">$1,499</div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Three layers of top-tier coating</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Multi-stage paint correction</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Complete vehicle protection</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Bi-annual inspections & maintenance</span>
                  </li>
                </ul>
                <button 
                  onClick={() => navigateToBookingWithPackage('ceramic_elite')}
                  className="w-full bg-gray-800 text-white py-2 rounded-lg font-medium hover:bg-gray-700 transition-all"
                >
                  Select Package
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 bg-gray-50 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Customer Testimonials</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See what our customers say about our ceramic coating services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "I had the premium ceramic coating applied to my Tesla and am absolutely blown away by the results. The depth and gloss are amazing, and water just beads off instantly. Worth every penny!"
              </p>
              <div className="font-medium text-gray-800">David M. - Chicago</div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "After two years with the coating, my car still looks freshly detailed! The maintenance has been so easy, and the protection against Chicago winters has been incredible."
              </p>
              <div className="font-medium text-gray-800">Jennifer K. - Evanston</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Ready to Protect Your Vehicle?</h2>
          <p className="mb-8 max-w-2xl mx-auto">
            Schedule your ceramic coating service today and enjoy years of protection and a stunning finish for your vehicle.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate('/book', { 
                state: { 
                  preSelectedService: 'ceramic'
                } 
              })}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-all shadow-md"
            >
              Book Now
            </button>
            <button 
              onClick={navigateToEstimate}
              className="bg-transparent border border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-all"
            >
              Get Free Estimate
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CeramicCoatingPage;